import { Box, Typography, InputAdornment, Button } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Address, formatEther, parseEther } from 'viem';
import TokenInput from '../common/TokenInput';

import BaseLogo from '../../assets/images/chains/BaseLogo.svg';
import EthLogo from '../../assets/images/chains/ethereum.png';
import VirtualTokenLogo from '../../assets/images/virtual-token.svg';
import { useVirtualBalanceContext } from '../../context/VirtualBalanceContext';
import { enqueueSnackbar } from 'notistack';
import {
  readContract,
  writeContract,
  waitForTransactionReceipt,
} from 'wagmi/actions';
import l1StandardBridge from '../../assets/abis/l1StandardBridge';
import { checkNetwork } from '../../shared/utils/CheckNetwork';
import { getTokenNetwork } from '../../shared/utils/TokenNetwork';
import { virtualTokenABI } from '../../assets/abis/virtualToken';
import { config } from '../../shared/constants/WagmiConfig';
import { useWallets } from '@privy-io/react-auth';
import { l2StandardBridge } from '../../assets/abis/l2StandardBridge';
import { initBridge } from '../../api/bridge.api';
import LoadingButton from '../common/LoadingButton';
import { truncateString } from '../../shared/utils/Formatter';

interface Props {
  type: 'deposit' | 'withdraw';
  isLoading: (loading: boolean) => void;
}

const BridgeInputGroup: React.FC<Props> = ({ type, isLoading }) => {
  const [bridgeAmount, setBridgeAmount] = useState<string>('');
  const { wallets } = useWallets();
  const {
    nativeVirtualBalance,
    virtualBalance,
    fetchVirtualBalance,
    fetchNativeVirtualBalance,
  } = useVirtualBalanceContext();

  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (type === 'withdraw') {
      fetchVirtualBalance();
    } else {
      fetchNativeVirtualBalance();
    }
  }, [type]);

  const performBridge = async () => {
    if (type === 'deposit') {
      await deposit();
    } else {
      await withdraw();
    }
  };

  const checkAllowance = async () => {
    const allowance = await readContract(config, {
      // @ts-ignore
      chainId: getTokenNetwork().id,
      address: process.env.REACT_APP_LOCAL_TOKEN as `0x${string}`,
      abi: virtualTokenABI,
      functionName: 'allowance',
      args: [
        wallets[0].address as `0x${string}`,
        process.env.REACT_APP_L1_STANDARD_BRIDGE as `0x${string}`,
      ],
    });

    if (Number(formatEther(allowance)) < Number(bridgeAmount)) {
      enqueueSnackbar({
        message: 'Please approve allowance to bridge',
        variant: 'info',
        persist: false,
      });

      await requestAllowance(BigInt(nativeVirtualBalance.toString()));
    }
  };

  const requestAllowance = async (approveWei: bigint) => {
    const txHash = await writeContract(config, {
      chainId: getTokenNetwork().id,
      address: process.env.REACT_APP_LOCAL_TOKEN as `0x${string}`,
      abi: virtualTokenABI,
      functionName: 'approve',
      args: [
        process.env.REACT_APP_L1_STANDARD_BRIDGE as `0x${string}`,
        approveWei,
      ],
    });

    await waitForTransactionReceipt(config, {
      chainId: getTokenNetwork().id,
      hash: txHash,
    });

    enqueueSnackbar({ message: 'Allowance approved', variant: 'success' });
  };

  const deposit = async () => {
    setLoading(true);

    try {
      await checkNetwork(getTokenNetwork());

      await checkAllowance();

      const txHash = await writeContract(config, {
        chainId: getTokenNetwork().id,
        abi: l1StandardBridge,
        address: process.env.REACT_APP_L1_STANDARD_BRIDGE as `0x${string}`,
        functionName: 'bridgeERC20',
        args: [
          process.env.REACT_APP_LOCAL_TOKEN,
          process.env.REACT_APP_REMOTE_TOKEN,
          parseEther(bridgeAmount.toString()),
          0,
          '0x',
        ],
      });

      await waitForTransactionReceipt(config, {
        chainId: getTokenNetwork().id,
        hash: txHash,
      });

      await initBridge('deposit', txHash);

      enqueueSnackbar({
        message:
          'Bridge success, you should see the fund in your wallet within the next 10 mins.',
        variant: 'success',
        action: (
          <a
            rel="noreferrer"
            target="_blank"
            href={`${
              getTokenNetwork().blockExplorers.default.url
            }/tx/${txHash}`}
          >
            <Button>View Tx</Button>
          </a>
        ),
      });

      fetchNativeVirtualBalance();

      setBridgeAmount('');
    } catch (error: any) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const withdraw = async () => {
    await checkNetwork();

    setLoading(true);

    try {
      const hash = await writeContract(config, {
        address: process.env.REACT_APP_L2_STANDARD_BRIDGE as Address,
        abi: l2StandardBridge,
        functionName: 'withdraw',
        args: [
          process.env.REACT_APP_REMOTE_TOKEN,
          parseEther(bridgeAmount),
          0,
          '0x',
        ],
      });

      await initBridge('withdraw', hash);

      enqueueSnackbar({
        message: 'Check history tab for bridge status',
        variant: 'info',
      });

      fetchVirtualBalance();

      setBridgeAmount('');
    } catch (error: any) {
      console.error(error);
      enqueueSnackbar({
        message: 'Failed to initiate bridge VIRTUAL from Base to Ethereum',
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const setMaxAmount = () => {
    const maxAmount = truncateString(
      formatEther(type === 'deposit' ? nativeVirtualBalance : virtualBalance),
      6
    );

    if (Number(maxAmount) > 0) {
      setBridgeAmount(Number(maxAmount).toString());
    }
  };

  useEffect(() => {
    isLoading(loading);
  }, [loading, isLoading]);

  return (
    <Box>
      <Box
        display="flex"
        flexDirection="column"
        bgcolor="#FBFBFB"
        sx={{ borderTopRightRadius: '1rem', borderTopLeftRadius: '1rem' }}
      >
        <Box display="flex" padding={2}>
          <Box flex={1} alignItems="center">
            <Typography fontSize={14} sx={{ marginBottom: 0.5, opacity: 0.5 }}>
              From
            </Typography>
            <Box display="flex" gap={1} alignItems="center">
              <img
                src={type === 'deposit' ? EthLogo : BaseLogo}
                alt="Deposit Currency"
                style={{ width: 20, height: 20 }}
              />
              <Typography>
                {type === 'deposit' ? 'Ethereum' : 'Base'}
              </Typography>
            </Box>
          </Box>
          <Box flex={1}>
            <Typography fontSize={14} sx={{ marginBottom: 0.5, opacity: 0.5 }}>
              To
            </Typography>
            <Box display="flex" gap={1} alignItems="center">
              <img
                src={type === 'deposit' ? BaseLogo : EthLogo}
                alt="Withdraw Currency"
                style={{ width: 20, height: 20 }}
              />
              <Typography>
                {type === 'deposit' ? 'Base' : 'Ethereum'}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box
          paddingX={2}
          paddingY={1}
          borderRadius={4}
          sx={{ border: '1px solid #DDD', backgroundColor: '#fff' }}
        >
          <Typography sx={{ opacity: 0.5 }} fontSize={14}>
            Amount of $VIRTUAL to Bridge
          </Typography>

          <TokenInput
            sx={{ marginBottom: 0 }}
            variant="standard"
            onChange={setBridgeAmount}
            value={bridgeAmount}
            startAdornment={
              <InputAdornment position="start">
                <img src={VirtualTokenLogo} alt="Virtual" />
              </InputAdornment>
            }
          />
        </Box>
      </Box>
      <Box display="flex" justifyContent="flex-end" gap={1} marginTop={1}>
        <Typography>
          {Number(
            formatEther(
              type === 'deposit' ? nativeVirtualBalance : virtualBalance
            )
          ).toLocaleString('en-US')}
        </Typography>
        <Typography sx={{ opacity: 0.5 }}>$VIRTUAL</Typography>

        <Button
          onClick={setMaxAmount}
          variant="contained"
          size="small"
          sx={{
            padding: '0 .6rem',
            minHeight: 0,
            minWidth: 0,
            fontSize: 12,
          }}
        >
          Max
        </Button>
      </Box>
      <LoadingButton
        loading={loading}
        onClick={performBridge}
        fullWidth
        type="button"
        variant="contained"
        size="large"
        sx={{ borderRadius: 2, marginTop: 3 }}
        disabled={
          Number(bridgeAmount) <= 0 ||
          isNaN(Number(bridgeAmount)) ||
          Number(bridgeAmount) >
            Number(
              formatEther(
                type === 'deposit' ? nativeVirtualBalance : virtualBalance
              )
            )
        }
      >
        {type === 'deposit' ? 'Deposit' : 'Withdraw'}
      </LoadingButton>
    </Box>
  );
};

export default BridgeInputGroup;
